<template>
  <div
    class="flex h-full w-full flex-col items-center justify-center gap-6 p-4 xl:p-8"
  >
    <h2 class="text-surface-0 text-xl font-semibold xl:text-2xl">
      Choose Model Type
    </h2>

    <div
      class="grid w-full max-w-4xl flex-1 grid-cols-1 gap-6 xl:grid-cols-2 xl:grid-rows-[2fr_1fr]"
    >
      <button
        v-for="option in options"
        :key="option.engine"
        type="button"
        class="border-surface-700 hover:border-surface-400 group relative overflow-hidden rounded-2xl border text-center transition-colors duration-200"
        :class="
          option.engine === 'model'
            ? 'flex flex-row items-center justify-center gap-4 p-4 xl:col-span-2'
            : 'flex flex-col items-center justify-center gap-4 p-8'
        "
        @click="emit('select', option.engine)"
      >
        <!-- Sample render fades in on hover; always faintly visible on touch screens -->
        <img
          :src="option.sample"
          alt=""
          class="pointer-events-none absolute inset-0 h-full w-full scale-105 object-cover opacity-0 transition-[opacity,transform] duration-500 ease-out group-hover:scale-100 group-hover:opacity-100 group-focus-visible:scale-100 group-focus-visible:opacity-100 [@media(hover:none)]:scale-100 [@media(hover:none)]:opacity-40"
        />
        <div
          class="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/90 via-black/55 to-black/30 opacity-0 transition-opacity duration-500 group-hover:opacity-100 group-focus-visible:opacity-100 [@media(hover:none)]:opacity-100"
        />

        <div
          class="relative flex items-center justify-center rounded-2xl"
          :class="[
            option.iconBg,
            option.engine === 'model' ? 'h-12 w-12' : 'h-16 w-16',
          ]"
        >
          <i
            :class="[
              option.icon,
              option.engine === 'model' ? 'text-xl' : 'text-3xl',
              option.iconColor,
            ]"
          />
        </div>

        <div
          class="relative flex flex-col gap-1 drop-shadow-[0_1px_6px_rgba(0,0,0,0.8)]"
          :class="{ 'text-left': option.engine === 'model' }"
        >
          <div class="text-surface-0 text-lg font-semibold xl:text-xl">
            {{ option.title }}
          </div>

          <div
            class="text-surface-400 group-hover:text-surface-200 text-sm transition-colors"
          >
            Support:
            <span class="text-surface-200 font-medium">
              {{ option.supports }}
            </span>
          </div>
        </div>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
const emit = defineEmits<{
  select: [engine: 'kiri' | 'meshy' | 'model'];
}>();

const SAMPLE_BASE = 'https://storage.googleapis.com/static.scan-the-world.com';

const options = [
  {
    engine: 'kiri' as const,
    title: '3D Gaussian Splats',
    supports: 'Image Set (20–300), Video',
    icon: 'pi pi-box',
    sample: `${SAMPLE_BASE}/3DGS_sample.png`,
    iconBg: 'bg-green-500/20',
    iconColor: 'text-green-400',
  },
  {
    engine: 'meshy' as const,
    title: 'AI Generated Mesh',
    supports: 'Image',
    icon: 'pi pi-sparkles',
    sample: `${SAMPLE_BASE}/meshy_sample.png`,
    iconBg: 'bg-blue-500/20',
    iconColor: 'text-blue-400',
  },
  {
    engine: 'model' as const,
    title: 'Directly Upload a Model',
    // .ksplat is intentionally omitted here even though Upload.vue still
    // accepts it for this engine - it's a deprecated format we don't want
    // to advertise as a supported option.
    supports: '.PLY, .GLB',
    icon: 'pi pi-upload',
    sample: `${SAMPLE_BASE}/GLB_sample.png`,
    iconBg: 'bg-purple-500/20',
    iconColor: 'text-purple-400',
  },
];
</script>
